import { openChatReplyPause } from "../hitl/actionPause";
import { getHITLState, type HITLState } from "../hitl/hitlUtils";

export interface QueuedUserMessageReadiness {
  isLoading?: boolean;
  isThinking?: boolean;
  hitlState?: HITLState | null;
  allowAfterLastUserMessage?: boolean;
  canCancelActiveTurn?: boolean;
}

const NEO_USER_ID = "neo";

const normalizedContentType = (message: any): string =>
  String(message?.content_type || "MESSAGE").trim().toUpperCase();

export function hasStreamingAssistantMessage(messages: any[] | null | undefined): boolean {
  return (messages || []).some(
    (message) => message?.user_id === NEO_USER_ID && message?.isStreaming === true
  );
}

function hasPendingUserMessage(messages: any[] | null | undefined): boolean {
  return (messages || []).some(
    (message) => message?.user_id !== NEO_USER_ID && message?.pending === true
  );
}

function lastMessage(messages: any[] | null | undefined): any | null {
  const list = messages || [];
  for (let index = list.length - 1; index >= 0; index -= 1) {
    if (list[index]) return list[index];
  }
  return null;
}

/**
 * Is the thread waiting on a card that a typed message answers (`chat_reply`)? Then the message is
 * the answer, sent right away -- not queued behind the card it answers.
 */
function isAwaitingChatReply(messages: any[] | null | undefined): boolean {
  const last = lastMessage(messages);
  return Boolean(last?.user_id === NEO_USER_ID && openChatReplyPause(last.human_review, last.human_review_status));
}

function isResolvedHumanReviewMessage(message: any): boolean {
  if (normalizedContentType(message) !== "HUMAN_REVIEW") return false;
  const status = String(message?.human_review_status || "").trim().toLowerCase();
  return status === "approved" || status === "canceled" || status === "scheduled";
}

function isCompletedAssistantTextMessage(message: any): boolean {
  if (!message || message.user_id !== NEO_USER_ID) return false;
  if (message.isStreaming === true || message.pending === true || message.error) return false;
  const contentType = normalizedContentType(message);
  if (contentType === "HUMAN_REVIEW") return false;
  if (contentType !== "MESSAGE" && contentType !== "PLAN_MESSAGE") return false;
  return typeof message.content === "string" && message.content.trim().length > 0;
}

function isConfirmedUserMessageAfterStop(message: any): boolean {
  if (!message || message.user_id === NEO_USER_ID) return false;
  if (message.pending === true || message.error || message.status === "failed") return false;
  return normalizedContentType(message) === "MESSAGE";
}

export function canSendQueuedUserMessage(
  messages: any[] | null | undefined,
  readiness: QueuedUserMessageReadiness = {}
): boolean {
  if (readiness.isLoading || readiness.isThinking) return false;
  if (hasStreamingAssistantMessage(messages)) return false;
  if (hasPendingUserMessage(messages)) return false;
  if (isAwaitingChatReply(messages)) return true;

  const hitl = getHITLState(messages || [], readiness.hitlState || undefined);
  if (hitl.hasPending) return false;

  const last = lastMessage(messages);
  if (!last) return true;

  if (last.user_id === NEO_USER_ID) {
    if (isResolvedHumanReviewMessage(last)) return false;
    return isCompletedAssistantTextMessage(last);
  }

  return Boolean(readiness.allowAfterLastUserMessage && isConfirmedUserMessageAfterStop(last));
}

export function canSendQueuedUserMessageNow(
  messages: any[] | null | undefined,
  readiness: QueuedUserMessageReadiness = {}
): boolean {
  if (readiness.isLoading || readiness.isThinking || hasStreamingAssistantMessage(messages)) {
    return Boolean(readiness.canCancelActiveTurn);
  }
  if (hasPendingUserMessage(messages)) return false;
  return true;
}

export function shouldQueueNewUserMessage(
  messages: any[] | null | undefined,
  readiness: QueuedUserMessageReadiness = {}
): boolean {
  if (readiness.isLoading || readiness.isThinking) return true;
  if (hasStreamingAssistantMessage(messages)) return true;
  if (hasPendingUserMessage(messages)) return true;
  if (isAwaitingChatReply(messages)) return false;

  const hitl = getHITLState(messages || [], readiness.hitlState || undefined);
  if (hitl.hasPending) return true;

  const last = lastMessage(messages);
  if (last?.user_id === NEO_USER_ID && isResolvedHumanReviewMessage(last)) {
    return false;
  }
  return last?.user_id === NEO_USER_ID && normalizedContentType(last) === "HUMAN_REVIEW";
}
